import React, { FormEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { FormikValues, useFormik } from 'formik';
import { consumeFacebookSelectAdAccountPrompt, startFacebookOAuth } from '../../../constants/integrationConstants';
import useGetOrganizationSettings from '../../../apiHooks/queries/useGetOrganizations';
import {
  GET_PROVIDER_DETAILS,
  ORGANIZATION_SETTINGS,
} from '../../../constants/pageIdentifiers';
import { AdProviderIDs, Colors } from '../../../constants/constants';
import SecondaryButton from '../../../components/buttons/secondaryButton';
import {
  useUpdateOrganizationSettings,
} from '../../../apiHooks/mutations/organizationMutations/organizationSettingsMutations';
import PrimaryButton from '../../../components/buttons/primaryButton';
import {
  facebookOrganizationSettingsValidationSchema,
} from '../../../validations/organizationSettingsFormValidationSchema';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import ProviderNumberBoxes from '../../../components/providerNumberBoxes/providerNumberBoxes';
import useGetAdProviderDetails from '../../../apiHooks/queries/adProviderDetails/useGetAdProviderDetails';
import useGetFacebookCustomers from '../../../apiHooks/queries/getFacebookCustomers/useGetFacebookCustomers';
import BasicDialog from '../../../dialogs/basicDialog';
import ConfirmDialog from '../../../dialogs/confirmDialog';
import metaImage from '../../../assets/images/dashboard/metaModalImg.png';
import TextInput from '../../../components/inputs/textInput';
import AutoCompleteInput from '../../../components/inputs/autoCompleteInput';
import useGetFacebookConversions from '../../../apiHooks/queries/facebookConversion/useGetFacebookConversion';
import {
  useSendDisconnectFacebookConversion, useSendFacebookAccessToken,
} from '../../../apiHooks/mutations/facebookConversionToken/changeFacebookConversionToken';
import { useDisconnectFacebook } from '../../../apiHooks/mutations/integrationMutations/facebookMutation';
import LoadingSpinner from '../../../components/loadingSpinner/loadingSpinner';
import RefreshIcon from '../../../assets/icons/refresh-icon.svg';

type FacebookCustomerOption = {
  id: string;
  name: string;
};

const FacebookPanel = () => {
  const { t } = useTranslation();
  const { setOrganizationSettings, organizationSettings: storedOrganizationSettings } = useOrganizationSettingsStore();
  const [tokenId, setTokenId] = useState<string>('');
  const [pixelId, setPixelId] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [adAccountSearch, setAdAccountSearch] = useState('');
  const [highlightAdAccount, setHighlightAdAccount] = useState(false);

  const {
    mutateAsync: updateOrganizationSettings,
    isLoading: isUpdatingOrganizationSettings,
  } = useUpdateOrganizationSettings();

  const { mutate: uninstallConversion } = useSendDisconnectFacebookConversion();
  const { mutate: sendAccessToken } = useSendFacebookAccessToken();
  const { mutateAsync: disconnectFacebook, isLoading: isDisconnectingFacebook } = useDisconnectFacebook();

  const {
    data,
    refetch: refetchOrganizationSettings,
    isLoading: isLoadingOrganizationSettings,
    isFetching: isFetchingOrganizationSettings,
  } = useGetOrganizationSettings(ORGANIZATION_SETTINGS);

  const {
    data: facebookCustomers,
    refetch: refetchFacebookCustomers,
    isLoading: isLoadingFacebookCustomers,
    isFetching: isFetchingFacebookCustomers,
  } = useGetFacebookCustomers('GET_CUSTOMERS');

  useGetFacebookConversions('FACEBOOK_CONVERSION');

  const organizationSettings = data?.data?.data || storedOrganizationSettings;

  useEffect(() => {
    if (data?.data?.data) {
      setOrganizationSettings(data.data.data);
    }
  }, [data?.data?.data, setOrganizationSettings]);

  useEffect(() => {
    if (consumeFacebookSelectAdAccountPrompt()) {
      setHighlightAdAccount(true);
    }
  }, []);

  const facebookCustomerId = organizationSettings?.facebookCustomerId
    ? String(organizationSettings.facebookCustomerId)
    : '';
  const customerIdOptions: FacebookCustomerOption[] = facebookCustomers?.data?.data || [];

  const formik: FormikValues = useFormik({
    initialValues: {
      facebookCustomerId,
    },
    validationSchema: () => facebookOrganizationSettingsValidationSchema(t),
    onSubmit: async (values: any) => {
      const normalizedValues = {
        ...values,
        facebookCustomerId: values.facebookCustomerId ? String(values.facebookCustomerId) : '',
      };
      await updateOrganizationSettings(normalizedValues);
      setOrganizationSettings({ ...organizationSettings, ...normalizedValues });
      refetchOrganizationSettings();
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (formik.values.facebookCustomerId) {
      setHighlightAdAccount(false);
    }
  }, [formik.values.facebookCustomerId]);

  useEffect(() => {
    if (!highlightAdAccount) {
      return undefined;
    }
    const timer = window.setTimeout(() => setHighlightAdAccount(false), 10000);
    return () => window.clearTimeout(timer);
  }, [highlightAdAccount]);

  const selectedAdAccount = useMemo(
    () => customerIdOptions.find((option) => String(option.id) === formik.values.facebookCustomerId) || null,
    [customerIdOptions, formik.values.facebookCustomerId],
  );

  const needsAdAccountSelection = Boolean(
    organizationSettings?.facebookToken && !formik.values.facebookCustomerId,
  );

  const isInitialLoading = isLoadingOrganizationSettings
    || (Boolean(organizationSettings?.facebookToken) && isLoadingFacebookCustomers);

  const isRefreshing = (isFetchingOrganizationSettings || isFetchingFacebookCustomers)
    && !isInitialLoading;

  const handleRefresh = () => {
    refetchOrganizationSettings();
    if (organizationSettings?.facebookToken) {
      refetchFacebookCustomers();
    }
  };

  const handleAdAccountChange = (_event: FormEvent, newValue: FacebookCustomerOption | null) => {
    formik.setFieldValue('facebookCustomerId', newValue ? String(newValue.id) : '');
    setAdAccountSearch('');
  };

  const handleAdAccountInputChange = (_event: SyntheticEvent, value: string) => {
    setAdAccountSearch(value);
    if (formik.values.facebookCustomerId) {
      formik.setFieldValue('facebookCustomerId', '');
    }
  };

  const { data: adProviderDetailsData, isFetching } = useGetAdProviderDetails(
    GET_PROVIDER_DETAILS,
    AdProviderIDs.FACEBOOK,
    !!organizationSettings?.facebookToken || false,
  );

  if (isInitialLoading) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center">
        <LoadingSpinner color="primary" size={50} center />
      </div>
    );
  }

  if (organizationSettings && organizationSettings.facebookToken) {
    return (
      <div className="relative flex w-full flex-col items-end">
        {isRefreshing && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
            <LoadingSpinner color="primary" size={50} center />
          </div>
        )}
        <ConfirmDialog
          variant="danger"
          open={isDisconnectDialogOpen}
          title="areYouSureYouWantToDisconnect"
          text="thisActionWillDisconnectMetaIntegration"
          onCloseDialog={() => setIsDisconnectDialogOpen(false)}
          onSubmit={() => {
            disconnectFacebook().then(() => {
              setIsDisconnectDialogOpen(false);
              refetchOrganizationSettings();
            });
          }}
          isDisabled={isDisconnectingFacebook}
        />
        {!organizationSettings?.isConnectedToFacebookConversionApi
                && (
                <PrimaryButton
                  className="mb-4 max-h-[40px]"
                  dataCy="bing-org-settings-submit"
                  onClick={() => setOpenModal(true)}
                >
                  {t('addTokenId')}
                </PrimaryButton>
                )}
        <div className="flex w-full flex-1 justify-between rounded-xl border p-4">
          <form onSubmit={formik.handleSubmit} className="flex gap-4">
            <Grid minWidth={300} item sm={12} md={6} lg={2.5}>
              <div
                className={`rounded-lg ${
                  needsAdAccountSelection || highlightAdAccount
                    ? `border-2 border-red p-0.5 ${highlightAdAccount ? 'animate-ad-account-blink' : ''}`
                    : ''
                }`}
              >
                <AutoCompleteInput
                  name="facebookCustomerId"
                  label="facebookCustomerId"
                  placeholder="selectAdAccount"
                  size="small"
                  fullWidth
                  options={customerIdOptions}
                  getOptionLabel={(option: FacebookCustomerOption) => option?.name || ''}
                  value={selectedAdAccount}
                  loading={isFetchingFacebookCustomers}
                  onChange={handleAdAccountChange}
                  inputValue={selectedAdAccount?.name || adAccountSearch}
                  onInputChange={handleAdAccountInputChange}
                  isOptionEqualToValue={(
                    option: FacebookCustomerOption,
                    value: FacebookCustomerOption,
                  ) => String(option.id) === String(value.id)}
                  error={Boolean(
                    needsAdAccountSelection
                    || (formik.touched.facebookCustomerId && formik.errors.facebookCustomerId),
                  )}
                  helperText={
                    needsAdAccountSelection
                      ? t('selectAdAccount')
                      : (formik.touched.facebookCustomerId && formik.errors.facebookCustomerId)
                  }
                />
              </div>
            </Grid>

            <Grid item sm={12} md={6} lg={2.5}>
              <div className="flex gap-2">
                <PrimaryButton
                  type="submit"
                  className="max-h-[40px]"
                  disabled={isUpdatingOrganizationSettings}
                  dataCy="bing-org-settings-submit"
                >
                  {t('save')}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex h-[40px] w-[40px] items-center justify-center rounded-lg border border-[#EAEAEA] disabled:opacity-50"
                  aria-label={String(t('refreshLatest'))}
                >
                  <img src={RefreshIcon} alt="" className="h-5 w-5 object-contain" />
                </button>
              </div>
            </Grid>
          </form>

          <div className="flex items-center justify-center gap-3 text-xs font-medium text-primary">
            <div>{t('accountStatus')}</div>
            <SecondaryButton
              style={{
                border: `1px solid ${needsAdAccountSelection ? Colors.red : Colors.green}`,
                color: needsAdAccountSelection ? Colors.red : Colors.green,
                height: '40px',
                fontSize: 14,
              }}
            >
              {needsAdAccountSelection ? t('notActive') : t('active')}
            </SecondaryButton>
          </div>
        </div>

        <div className="mt-4 flex w-full justify-between gap-4 rounded-xl border p-4">
          <ProviderNumberBoxes
            isLoading={isFetching}
            tracked
            stats={adProviderDetailsData?.data?.data?.ads_tracked || 0}
          />
          <ProviderNumberBoxes
            isLoading={isFetching}
            revenue
            stats={adProviderDetailsData?.data?.data?.revenue_tracked || 0}
          />
          <ProviderNumberBoxes
            isLoading={isFetching}
            leads
            stats={adProviderDetailsData?.data?.data?.leads_attributed || 0}
          />
        </div>
        <div className="mt-4 flex w-full items-center justify-between gap-4 rounded-xl border p-4">
          <div className="flex flex-col gap-2 text-sm font-medium text-primary">
            <p className="font-semibold">{t('disconnectMetaIntegration')}</p>
            <p>{t('removeIntegration')}</p>
          </div>
          <button
            type="button"
            onClick={() => setIsDisconnectDialogOpen(true)}
            className="rounded-[6px] bg-[#F8D8D6] px-8 py-3 text-[#DB3A34]"
          >
            {t('disconnectFromMeta')}
          </button>
        </div>
        {organizationSettings?.isConnectedToFacebookConversionApi
                && (
                <div className="mt-4 flex w-full items-center justify-between gap-4 rounded-xl border p-4">
                  <div className="flex flex-col gap-2 text-sm font-medium text-primary">
                    <p className="font-semibold">{t('disconnectTokenId')}</p>
                    <p>{t('removeIntegration')}</p>
                  </div>
                  <button onClick={() => { uninstallConversion(); refetchOrganizationSettings(); }} className="rounded-[6px] bg-[#F8D8D6] px-8 py-3 text-[#DB3A34]">{t('disconnectTokenId')}</button>
                </div>
                )}
        <BasicDialog
          allowCloseOnBackdrop
          closeDialog={() => setOpenModal(false)}
          open={openModal}
          title="addTokenId"
          headerColor="white"
          trackingModal
        >
          <div className="flex flex-col items-center gap-4">
            <img src={metaImage} alt="" className="max-w-[200px]" />
            <p className="text-center text-primary">{t('facebookAccessTokenReminder')}</p>
          </div>
          <div className="mt-4 flex flex-col gap-4 pb-4">
            <TextInput
              name="Access Token"
              value={tokenId}
              label={t('accessToken')}
              size="small"
              fullWidth
              placeholder={t('accessTokenPlaceholder')}
              onChange={(e) => setTokenId(e.target.value)}
            />
            <TextInput
              name="Pixel Token"
              value={pixelId}
              label={t('pixelToken')}
              size="small"
              fullWidth
              placeholder={t('pixelTokenPlaceholder')}
              onChange={(e) => setPixelId(e.target.value)}
            />
            <PrimaryButton
              className="max-h-[40px] w-full"
              dataCy="bing-org-settings-submit"
              onClick={() => {
                sendAccessToken({
                  tokenId,
                  pixelId,
                });
                refetchOrganizationSettings();
                setOpenModal(false);
              }}
            >
              {t('create')}
            </PrimaryButton>
          </div>
        </BasicDialog>
      </div>
    );
  }

  return (
    <div className="flex justify-between gap-5 rounded-xl border p-4">
      <div className="flex flex-col gap-2 text-primary">
        <span className="text-sm font-semibold">{t('connectToMeta')}</span>

        <p className="text-sm font-normal">
          {t('metaPanelDescription')}
        </p>
      </div>
      <button
        type="button"
        className="flex items-center gap-2.5 rounded-lg bg-primary px-5 py-2
        text-sm text-white duration-200 focus:outline-[1px] focus:outline-offset-[3px] focus:outline-primary/50 enabled:hover:bg-[#0a3a4b]
        enabled:active:bg-opacity-80 disabled:opacity-50"
        onClick={() => {
          startFacebookOAuth();
        }}
      >
        {t('connectToMeta')}
      </button>
    </div>
  );
};

export default FacebookPanel;
